import React from 'react';
import type { WorkflowResponse } from '@/modules/workflows/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import {
  getSkillMeta,
  formatSkillDate,
  SkillAvatar,
  SkillStatusBadge,
} from './skillShared';

/**
 * Linha de tabela de skill (view "Lista"). Padrão-lista travado:
 * checkbox (1ª col) + coluna multi-linha (nome+descrição) + menu ••• (última col).
 */
export interface SkillListItemProps {
  skill: WorkflowResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onExport: (id: string) => void;
  onDelete: (id: string) => void;
  onActivate: (id: string) => void;
  onPause: (id: string) => void;
}

export const SkillListItem: React.FC<SkillListItemProps> = ({
  skill,
  selected = false,
  onToggleSelect,
  onEdit,
  onDuplicate,
  onExport,
  onDelete,
  onActivate,
  onPause,
}) => {
  const { description, executionMode, nodeCount } = getSkillMeta(skill);
  const isActive = skill.status === 'active';

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(skill.id) },
    { label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(skill.id) },
    { label: 'Exportar', icon: 'download', onClick: () => onExport(skill.id) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'pause' : 'play',
      onClick: () => (isActive ? onPause(skill.id) : onActivate(skill.id)),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(skill.id), variant: 'danger' },
  ];

  // Drag pro FolderList (mesmo contrato do WorkflowListItem — 'workflow-id')
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('workflow-id', skill.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
      }}
      draggable
      onDragStart={handleDragStart}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      data-testid={`skill-row-${skill.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(skill.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`skill-check-${skill.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Skill: avatar + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0">
            <SkillAvatar size="sm" />
          </div>
          <button type="button" onClick={() => onEdit(skill.id)} className="min-w-0 text-left">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {skill.name}
            </div>
            {description && (
              <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                {description}
              </div>
            )}
          </button>
        </div>
      </td>

      {/* Status — F47: switch inline (Switch shared sm) para ligar/desligar;
          pill só nos status não-toggláveis (rascunho/arquivado) */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {isActive || skill.status === 'inactive' ? (
          <div className="inline-flex items-center gap-2" data-testid={`skill-status-toggle-${skill.id}`}>
            <Switch
              size="sm"
              checked={isActive}
              onChange={() => (isActive ? onPause(skill.id) : onActivate(skill.id))}
            />
            <span
              className="text-xs font-semibold"
              style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}
            >
              {isActive ? 'Ativo' : 'Pausado'}
            </span>
          </div>
        ) : (
          <SkillStatusBadge status={skill.status} data-testid={`skill-status-${skill.id}`} />
        )}
      </td>

      {/* Modo */}
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          <Icon name="zap" className="w-3.5 h-3.5" />
          {executionMode === 'sync' ? 'Síncrono' : 'Assíncrono'}
        </span>
      </td>

      {/* Etapas */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        <span className="inline-flex items-center gap-1.5">
          <Icon name="activity" className="w-3.5 h-3.5" />
          {nodeCount} {nodeCount === 1 ? 'etapa' : 'etapas'}
        </span>
      </td>

      {/* Atualizado */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        {formatSkillDate(skill.updatedAt)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`skill-actions-${skill.id}`} />
      </td>
    </tr>
  );
};

SkillListItem.displayName = 'SkillListItem';
